import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

type Params = Record<string, string | undefined>;

type HistoryCompat = {
  push: (to: any, state?: any) => void;
  replace: (to: any, state?: any) => void;
  goBack: () => void;
  location: ReturnType<typeof useLocation>;
};

type MatchCompat = {
  params: Params;
  path: string;
  url: string;
};

export type RouterCompatProps = {
  match?: MatchCompat;
  location?: ReturnType<typeof useLocation>;
  history?: HistoryCompat;
  params?: Params;
};

const normalizeTo = (to: any) => {
  if (typeof to === 'string') return to;
  if (to && typeof to === 'object') {
    const { pathname, search, hash } = to;
    if (pathname || search || hash) {
      return { pathname, search, hash };
    }
  }
  return to;
};

export const useRouterCompat = (): RouterCompatProps => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams() as Params;

  const history = React.useMemo<HistoryCompat>(() => {
    return {
      push: (to, state) => navigate(normalizeTo(to), { state }),
      replace: (to, state) => navigate(normalizeTo(to), { replace: true, state }),
      goBack: () => navigate(-1),
      location
    };
  }, [navigate, location]);

  const match = React.useMemo<MatchCompat>(() => {
    return {
      params,
      path: location.pathname,
      url: location.pathname
    };
  }, [params, location.pathname]);

  return { match, location, history, params };
};

export const useRouteMatchCompat = (): MatchCompat => {
  return useRouterCompat().match;
};

export const withRouter = (Component: React.ComponentType<any>) => {
  const Wrapped = (props: any) => {
    const router = useRouterCompat();
    return <Component {...props} {...router} />;
  };

  Wrapped.displayName = `withRouter(${Component.displayName || Component.name || 'Component'})`;

  return Wrapped;
};
